import React from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Stack,
  useTheme
} from '@mui/material';
import { 
  FitnessCenter, 
  AccessTime, 
  Group, 
  LocalOffer
} from '@mui/icons-material';
import logo_color from '../assets/the_burn_primary_logo_fullcolor_RGB.png';

// Types
interface PricingPlan {
  id: string;
  title: string;
  price: string;
  length: string;
  guestPasses: number;
  url: string;
  features: string[];
  popular?: boolean;
}

interface DropInOption {
  id: string;
  title: string;
  price: string;
  length: string;
  expires: string;
  description: string;
  url: string;
  features: string[];
}

const Membership: React.FC = () => {
  const theme = useTheme();

  // Unlimited Membership Options
  const unlimitedPlans: PricingPlan[] = [
    {
      id: "1",
      title: "Month-to-Month",
      price: "$135",
      length: "per month",
      guestPasses: 0,
      url: "https://app.fitdegree.com/t/dashboard/fitspot/873/store/6",
      features: ["Unlimited classes", "Flexible cancellation", "No commitment"]
    },
    {
      id: "2",
      title: "6 Month Commitment",
      price: "$120",
      length: "per month",
      guestPasses: 2,
      url: "https://app.fitdegree.com/t/dashboard/fitspot/873/store/6",
      features: ["Unlimited classes", "2 guest passes included", "Pre-pay discounts available"],
      popular: true
    },
    {
      id: "3",
      title: "12 Month Commitment",
      price: "$100",
      length: "per month",
      guestPasses: 6,
      url: "https://app.fitdegree.com/t/dashboard/fitspot/873/store/6",
      features: ["Unlimited classes", "6 guest passes included", "Pre-pay discounts available"]
    },
  ];

  // Limited Membership Options
  const limitedPlans: PricingPlan[] = [
    {
      id: "4",
      title: "4X Per Month",
      price: "$65",
      length: "per month",
      guestPasses: 0,
      url: "https://share.fitdegree.com/?share=67a66949a670b",
      features: ["4 classes per month", "Perfect for busy schedules", "Rollover options"]
    },
  ];

  // Other Options
  const otherOptions: DropInOption[] = [
    {
      id: "1",
      title: "Drop-In",
      price: "$25",
      length: "30 days",
      expires: "30 days after purchase",
      description: "Single class to use",
      url: "https://www.theburnstudio.com/classes/schedule",
      features: ["Perfect for one-time visits", "No commitment", "Immediate access"]
    },
    {
      id: "2",
      title: "1 Week Unlimited",
      price: "$39",
      length: "7 days",
      expires: "7 days after first use",
      description: "Unlimited classes for 7 days",
      url: "https://app.fitdegree.com/t/dashboard/fitspot/873/store/7",
      features: ["Unlimited classes for 7 days", "Access to all class types", "No commitment"]
    },
    {
      id: "3",
      title: "10 Class Pack",
      price: "$185",
      length: "12 months",
      expires: "12 months after purchase",
      description: "10 classes to use at your leisure",
      url: "https://app.fitdegree.com/t/dashboard/fitspot/873/store/7",
      features: ["Flexible scheduling", "Great for occasional visitors", "Long expiration"]
    },
  ];

  const handlePlanClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#E3F2FD', // Light blue background
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 2 }}>
        {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          color="secondary" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Choose Your Fitness Journey
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Find your fire with our flexible membership options. 
          From drop-in classes to unlimited access, we have a plan that fits your lifestyle.
        </Typography>
      </Box>

      {/* Unlimited Membership */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          color="secondary" 
          sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
        >
          Unlimited Membership
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {unlimitedPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card 
                elevation={plan.popular ? 8 : 2}
                sx={{ 
                  height: '100%',
                  transform: plan.popular ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    elevation: 12
                  },
                  border: plan.popular ? `2px solid ${theme.palette.primary.main}` : 'none',
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h4" component="h3" gutterBottom fontWeight={600}>
                    {plan.title}
                  </Typography>
                  <Typography variant="h3" component="div" color="secondary" gutterBottom fontWeight={700}>
                    {plan.price}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {plan.length}
                  </Typography>
                  
                  {plan.guestPasses > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Group sx={{ mr: 1, color: 'secondary.main' }} />
                      <Typography variant="body2" color="secondary">
                        {plan.guestPasses} Guest Passes
                      </Typography>
                    </Box>
                  )}

                  <Stack spacing={1} sx={{ mt: 3 }}>
                    {plan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FitnessCenter sx={{ mr: 1, color: 'success.main', fontSize: 20 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large"
                    onClick={() => handlePlanClick(plan.url)}
                    sx={{ 
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Limited Membership */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          color="secondary" 
          sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
        >
          Limited Membership
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {limitedPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    elevation: 8
                  },
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h4" component="h3" gutterBottom fontWeight={600}>
                    {plan.title}
                  </Typography>
                  <Typography variant="h3" component="div" color="secondary" gutterBottom fontWeight={700}>
                    {plan.price}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {plan.length}
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 3 }}>
                    {plan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FitnessCenter sx={{ mr: 1, color: 'success.main', fontSize: 20 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large"
                    onClick={() => handlePlanClick(plan.url)}
                    sx={{ 
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Other Options */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          color="secondary" 
          sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
        >
          Other Options
        </Typography>
        <Grid container spacing={3}>
          {otherOptions.map((option, index) => (
            <Grid item xs={12} sm={6} md={4} key={option.id}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    elevation: 8
                  },
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <LocalOffer sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5" component="h3" fontWeight={600}>
                      {option.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" color="secondary" gutterBottom fontWeight={700}>
                    {option.price}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Valid for {option.length}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {option.description}
                  </Typography>

                  <Stack spacing={1} sx={{ mb: 3 }}>
                    {option.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTime sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    fullWidth 
                    size="large"
                    onClick={() => handlePlanClick(option.url)}
                    sx={{ 
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Book Now
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Student Discount Notice */}
      <Box sx={{ 
        textAlign: 'center', 
        p: 4, 
        bgcolor: 'grey.50', 
        borderRadius: 2,
        mb: 6
      }}>
        <Typography variant="h6" color="secondary" gutterBottom fontWeight={600}>
          Student Discounts Available
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Save up to 20% with a valid student ID. Contact us for more information and verification.
        </Typography>
      </Box>

      {/* Logo Section */}
      <Box sx={{ textAlign: 'center' }}>
        <Box
          component="img"
          src={logo_color}
          alt="The Burn Studio Logo"
          sx={{
            height: 'auto',
            width: '100%',
            maxWidth: 400,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
          }}
        />
      </Box>
      </Container>
    </Box>
  );
};

export default Membership;
